import styled from 'styled-components';

// Define colors for each type with separate background and border colors
const TYPE_COLORS = {
  rewards: {
    background: '#F0FDF4',
    border: '#B9F8CF',
    main: '#00A63E',
  },
  consequences: {
    background: '#FEF2F2',
    border: '#FFC9C9',
    main: '#E7000B',
  },
  obstacles: {
    background: '#FFFBEB',
    border: '#FEE685',
    main: '#E17100',
  },
  solutions: {
    background: '#EFF6FF',
    border: '#BEDBFF',
    main: '#155DFC',
  },
  default: {
    background: '#F8FAFC',
    border: '#E2E8F0',
    main: '#000000',
  },
};

// Get colors based on type
const getTypeColors = (type: string) => {
  return TYPE_COLORS[type as keyof typeof TYPE_COLORS] || TYPE_COLORS.default;
};

export const Wrapper = styled.div<{ type: string; isEditing?: boolean }>`
  display: flex;
  flex-direction: column;
  padding: 22px 22px 16px 22px;
  border-radius: 12px;
  border: 2px solid transparent;
  gap: 16px;
  height: 100%;
  transition: background-color 0.3s, border-color 0.3s;

  background-color: ${({ type }) => getTypeColors(type).background};
  border-color: ${({ type }) => getTypeColors(type).border};

  &:hover {
    background-color: ${({ type }) => getTypeColors(type).background};
    border-color: ${({ type }) => getTypeColors(type).border};
  }
  
  ${({ theme, type }) => theme.max("md")`
    background-color: ${getTypeColors(type).background};
    border-color: ${getTypeColors(type).border};
  `}
`;

export const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  width: 100%;
`;

export const IconWrapper = styled.div`
  width: 16px;
  height: 16px;
  display: flex;
  justify-content: center;
  align-items: center;
  margin-right: 8px;
`;

export const Icon = styled.img`
  width: 16px;
  height: 16px;
`;

export const TextContainer = styled.div`
  display: flex;
  flex-direction: column;
  text-align: left;
  gap: 4px;
`;

export const MainText = styled.span<{ type: string }>`
  color: ${({ type }) => getTypeColors(type).main};
  font-family: Inter;
  font-size: 16px;
  font-weight: 600;
  line-height: 24px;
`;

export const SubText = styled.span`
  color: #6A7282;
  font-family: Inter;
  font-size: 12px;
  line-height: 15px;
`;

export const SaveButton = styled.button`
  border-radius: 8px;
  background: #1271A6;
  border: none;
  padding: 9px 13px;
  color: #fff;
  text-align: center;
  font-family: Inter;
  font-size: 12px;
  font-weight: 500;
  line-height: 16px;
  transition: color 0.15s ease-in-out, background-color 0.15s ease-in-out, border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;

  &:disabled {
    opacity: 0.6;
  }

  &:focus {
    box-shadow: rgba(18, 113, 166, 0.498) 0px 0px 0px 0.25rem;
  }
`;

export const CancelButton = styled.button`
  border-radius: 8px;
  border: 1px solid rgba(0, 0, 0, 0.10);
  background: #FFF;
  padding: 9px 13px;
  color: #0A0A0A;
  text-align: center;
  font-family: Inter;
  font-size: 12px;
  font-weight: 500;
  line-height: 16px;

  &:disabled {
    opacity: 0.6;
  }
`;

export const ButtonsWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

export const Body = styled.div<{ isEditing?: boolean }>`
  flex: 1;
  padding: 0 0 10px 0;
`;

export const BodyText = styled.p<{ isExample?: boolean; hasValue?: boolean }>`
  padding: 17px 17.5px 17px 17px;
  border-radius: 10px;
  border: 1px solid;
  background: ${({ hasValue }) => hasValue ? 'transparent' : 'rgba(255, 255, 255, 0.80)'};
  border-color: ${({ hasValue }) => hasValue ? 'transparent' : 'rgba(255, 255, 255, 0.50)'};
  text-align: left;
  color: #4A5565;
  font-family: Inter;
  font-size: 14px;
  font-style: ${({ isExample }) => isExample ? 'italic' : 'normal'};
  line-height: 22.75px;
  word-break: break-word;
  height: 100%;
  cursor: pointer;
  transition: background 0.2s ease, border-color 0.2s ease;

  &:hover {
    background: ${({ hasValue }) => hasValue ? 'rgba(255, 255, 255, 0.80)' : 'rgba(255, 255, 255, 0.80)'};
  }
`;

export const Footer = styled.div<{ isExample?: boolean }>`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

export const FooterText = styled.span`
  color: #64748B;
  font-family: Inter;
  font-size: 14px;
  line-height: 21px;
  cursor: pointer;
  opacity: 0.6;
  
  &:hover {
    color: #4A5565;
  }
`;

export const FooterIcon = styled.img`
  width: 16px;
  height: 16px;
  cursor: pointer;
  opacity: 0.7;
  
  &:hover {
    opacity: 1;
  }
`;

export const AIButton = styled.button`
  display: flex;
  align-items: center;
  gap: 4px;
  border-radius: 8px;
  border: 1px solid transparent;
  background: transparent;
  padding: 9px 13px;
  color: #1271A6;
  text-align: center;
  font-family: Inter;
  font-size: 12px;
  font-weight: 500;
  line-height: 16px;
  cursor: pointer;
  transition: color 0.15s ease-in-out, background-color 0.15s ease-in-out, border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;

  &:hover {
    background-color: #f8f9fa;
    border-color: #1271A6;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  &:focus {
    box-shadow: rgba(18, 113, 166, 0.498) 0px 0px 0px 0.25rem;
  }
`;

export const AIButtonIcon = styled.img`
  width: 16px;
  height: 16px;
`;

export const AIButtonText = styled.span`
  color: #1271A6;
  font-family: Inter;
  font-size: 12px;
  font-weight: 500;
  line-height: 16px;
`;

// Styled wrapper for InputAreaAI to apply custom styles only in this component
export const StyledInputAreaAI = styled.div`
  height: 100%;
  
  & textarea {
    border-radius: 10px !important;
    border: 2px solid #E5E7EB !important;
    padding: 17px 17.5px 17px 17px !important;
    font-size: 14px !important;
    line-height: 22px !important;
  }
`;
